import React, { Component } from "react";
import _ from "lodash";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { debounce } from "throttle-debounce";
import RegistrationsService from "../../RegistrationsService";

/**
 * MN-2075: Attendance Report.
 *
 * Both find modes exist only to build one thing - a set of child_template ids. Everything
 * downstream (the report call, grouping by member, export) is keyed on that set and is
 * mode-agnostic, which is why "find by class" could be added without touching the endpoint.
 */

const NoDataIndication = () => (
  <div className="container h-100">
    <div className="row NoDataFoundTable">
      <h5>No Data Found!</h5>
    </div>
  </div>
);

const getExportFileName = (filetype) => {
  const formattedDate = new Date()
    .toLocaleDateString("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, ".");
  return `Attendance Report ${formattedDate}.${filetype}`;
};

const MySearch = (props) => {
  let input;
  return (
    <form role="search" className="app-search px-2 my-2">
      <div className="form-group mb-0">
        <input
          ref={(n) => (input = n)}
          type="text"
          id="AttendanceReportSearch"
          className="form-control rounded border mb-1 py-2 w-100"
          placeholder="Member Search.."
          autoComplete="off"
          onChange={() => props.onSearch(input.value)}
        />
        <button
          type="button"
          className="c-pointer text-secondary bg-transparent border-0"
        >
          <i className="fa fa-search" />
        </button>
      </div>
    </form>
  );
};

const MyExportCSV = (props) => (
  <div>
    <i
      title="Export to csv"
      className="csvIconSize fas fa-file-csv pl-1 c-pointer"
      onClick={() => props.onExport()}
    ></i>
  </div>
);

const MyExportPDF = (props) => {
  const handleClick = () => {
    const { data } = props;
    if (_.isEmpty(data)) {
      return;
    }
    const tableBody = data.map((val) => [
      val.firstName,
      val.lastName,
      val.memberNumber,
      val.region,
      val.facility,
      val.email,
      `${val.attendedCount} of ${val.totalClasses}`,
      val.classesAttended,
    ]);
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "FIRST NAME",
          "LAST NAME",
          "MEMBER NUMBER",
          "REGION",
          "FACILITY",
          "EMAIL",
          "CLASSES ATTENDED",
          "CLASSES",
        ],
      ],
      body: tableBody,
      tableWidth: "auto",
      styles: { cellPadding: 0.7, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 18 },
      },
    });
    doc.save(getExportFileName("pdf"));
  };

  return (
    <div>
      <i
        title="Export to pdf"
        className="csvIconSize fas fa-file-pdf pl-1 c-pointer"
        onClick={handleClick}
      >
        {" "}
      </i>
    </div>
  );
};

const columns = [
  { text: "FIRST NAME", dataField: "firstName", sort: true },
  { text: "LAST NAME", dataField: "lastName", sort: true },
  { text: "MEMBER NUMBER", dataField: "memberNumber", sort: true },
  { text: "REGION", dataField: "region", sort: true },
  { text: "FACILITY", dataField: "facility", sort: true },
  { text: "EMAIL", dataField: "email", sort: true },
  {
    // Numeric under the hood so sorting is by count, not by the "2 of 5" string.
    text: "CLASSES ATTENDED",
    dataField: "attendedCount",
    sort: true,
    formatter: (cell, row) => `${cell} of ${row.totalClasses}`,
    csvFormatter: (cell, row) => `${cell} of ${row.totalClasses}`,
  },
  { text: "CLASSES", dataField: "classesAttended", sort: false },
  // Always "Attended" in v1; kept so a future No-Show view is a filter change, not a rebuild.
  { text: "STATUS", dataField: "registrationStatus", sort: true },
];

export class AttendanceReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      findBy: "course", // "course" | "class"
      courses: [],
      selectedCourse: null,
      // Course mode: classes gathered from every added course, each carrying `selected`.
      classOptions: [],
      isLoadingClasses: false,
      // Class mode: cross-course picks, rendered as removable chips.
      classSearchOptions: [],
      selectedClasses: [],
      isSearchingClasses: false,
      // Results
      memberRows: [],
      isLoadingReport: false,
      hasRun: false,
      // The class count the run was made against, so "X of N" cannot drift when the
      // selection is edited after a run.
      ranAgainstClassCount: 0,
    };
    this.searchClassesDebounced = debounce(500, this.searchClasses);
  }

  async componentDidMount() {
    await this.loadCourses();
  }

  loadCourses = async () => {
    let data = await RegistrationsService.GetAdminTemplates();
    if (!_.isEmpty(data) && data.statusCode == 200) {
      this.setState({ courses: data.result != null ? data.result : [] });
    }
  };

  /** Any change to the selection invalidates the last run, so stale rows never linger. */
  resetResults = (nextState) => {
    this.setState({ ...nextState, memberRows: [], hasRun: false });
  };

  //#region Course mode

  /** Adds the picked course's classes to the checklist, all ticked, skipping duplicates. */
  addCourseClasses = async (course) => {
    if (course == null) {
      return;
    }
    this.setState({ isLoadingClasses: true });
    let data = await RegistrationsService.GetCourseClasses(course.id);
    let classes = !_.isEmpty(data) && data.result != null ? data.result : [];

    let existingIds = this.state.classOptions.map((c) => c.id);
    let added = classes
      .filter((c) => !existingIds.includes(c.id))
      .map((c) => ({ ...c, selected: true }));

    this.resetResults({
      classOptions: [...this.state.classOptions, ...added],
      isLoadingClasses: false,
    });
  };

  courseChangeHandler = async (e, newValue) => {
    this.setState({ selectedCourse: newValue });
    await this.addCourseClasses(newValue);
  };

  toggleClass = (classId) => {
    this.resetResults({
      classOptions: this.state.classOptions.map((c) =>
        c.id === classId ? { ...c, selected: !c.selected } : c
      ),
    });
  };

  setAllClasses = (selected) => {
    this.resetResults({
      classOptions: this.state.classOptions.map((c) => ({ ...c, selected })),
    });
  };

  clearSelection = () => {
    this.resetResults({ selectedCourse: null, classOptions: [] });
  };

  //#endregion

  //#region Class mode

  searchClasses = async (term) => {
    this.setState({ isSearchingClasses: true });
    let data = await RegistrationsService.SearchClasses(term);
    this.setState({
      classSearchOptions:
        !_.isEmpty(data) && data.result != null ? data.result : [],
      isSearchingClasses: false,
    });
  };

  classesChangeHandler = (e, newValue) => {
    this.resetResults({ selectedClasses: newValue });
  };

  //#endregion

  switchFindBy = (findBy) => {
    // Each mode keeps its own selection; only the results are invalidated.
    this.resetResults({ findBy });
  };

  /** The report's only real input, whichever mode built it. */
  getSelectedClasses = () => {
    return this.state.findBy === "course"
      ? this.state.classOptions.filter((c) => c.selected)
      : this.state.selectedClasses;
  };

  /**
   * One row per (member, class attended) collapses to one row per member.
   * Distinct class ids guard against a member holding more than one attended
   * registration for the same class, which would otherwise inflate "X of N".
   */
  buildMemberRows = (rows, totalClasses) => {
    return _.map(_.groupBy(rows, "memberId"), (memberRows) => {
      let first = memberRows[0];
      let distinctClasses = _.uniqBy(memberRows, "childTemplateId");
      return {
        memberId: first.memberId,
        firstName: first.firstName,
        lastName: first.lastName,
        memberNumber: first.memberNumber,
        region: first.region,
        facility: first.facility,
        email: first.email,
        registrationStatus: first.registrationStatus,
        attendedCount: distinctClasses.length,
        totalClasses: totalClasses,
        classesAttended: distinctClasses.map((r) => r.className).join("; "),
      };
    });
  };

  runReport = async () => {
    let selected = this.getSelectedClasses();
    if (selected.length === 0) {
      return;
    }
    this.setState({ isLoadingReport: true });

    let data = await RegistrationsService.GetAttendanceReport(
      selected.map((c) => c.id)
    );
    let rows = !_.isEmpty(data) && data.result != null ? data.result : [];

    this.setState({
      memberRows: this.buildMemberRows(rows, selected.length),
      ranAgainstClassCount: selected.length,
      isLoadingReport: false,
      hasRun: true,
    });
  };

  renderCourseMode() {
    let { courses, selectedCourse, classOptions, isLoadingClasses } = this.state;
    let selectedCount = this.getSelectedClasses().length;

    return (
      <>
        <div className="row">
          <div className="col-lg-6">
            <Autocomplete
              id="reportCourse"
              options={courses}
              getOptionLabel={(option) =>
                typeof option === "object" ? option.name : ""
              }
              onChange={this.courseChangeHandler}
              value={selectedCourse}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Course"
                  variant="standard"
                  placeholder="Select a course"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <small className="text-muted">
              Pick a course to list its classes. Pick another to add its classes
              to the same list.
            </small>
          </div>
        </div>

        {isLoadingClasses && (
          <div className="mt-3 text-muted">Loading classes&hellip;</div>
        )}

        {classOptions.length > 0 && (
          <div className="mt-4">
            <div className="d-flex align-items-center mb-2">
              <h6 className="mb-0">
                Classes ({selectedCount} of {classOptions.length} selected)
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-link ml-3"
                onClick={() => this.setAllClasses(true)}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => this.setAllClasses(false)}
              >
                Clear all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link text-danger ml-auto"
                onClick={this.clearSelection}
              >
                Start over
              </button>
            </div>

            <div className="card">
              <div className="card-body py-2">
                {classOptions.map((c) => (
                  <div className="form-check py-1" key={c.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`class_${c.id}`}
                      checked={c.selected}
                      onChange={() => this.toggleClass(c.id)}
                    />
                    <label
                      className="form-check-label c-pointer"
                      htmlFor={`class_${c.id}`}
                    >
                      {c.name}
                      <small className="text-muted ml-2">
                        {c.courseName}
                        {c.dates ? ` · ${c.dates}` : ""}
                      </small>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  renderClassMode() {
    let { classSearchOptions, selectedClasses, isSearchingClasses } = this.state;

    return (
      <div className="row">
        <div className="col-lg-8">
          <Autocomplete
            multiple
            id="reportClasses"
            options={classSearchOptions}
            value={selectedClasses}
            filterSelectedOptions
            // The server already filters (class name OR course name, trimmed). MUI's default client-side
            // filter re-filters on the class name only, which silently discards every course-name match
            // and any term with surrounding spaces - so hand the server's list through untouched.
            // (filterSelectedOptions still hides already-picked classes; MUI applies it separately.)
            filterOptions={(options) => options}
            loading={isSearchingClasses}
            getOptionLabel={(option) =>
              typeof option === "object" ? option.name : ""
            }
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={this.classesChangeHandler}
            onInputChange={(e, value) => this.searchClassesDebounced(value)}
            onOpen={() => {
              if (classSearchOptions.length === 0) {
                this.searchClasses("");
              }
            }}
            renderOption={(option) => (
              <div>
                <div>{option.name}</div>
                <small className="text-muted">
                  {option.courseName}
                  {option.dates ? ` · ${option.dates}` : ""}
                </small>
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Classes"
                variant="standard"
                placeholder="Search classes by name or course"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <small className="text-muted">
            Search and pick classes directly - they can come from different
            courses.
          </small>
        </div>
      </div>
    );
  }

  renderSelectionSummary() {
    let selected = this.getSelectedClasses();
    if (selected.length === 0) {
      return null;
    }
    let courseCount = _.uniq(selected.map((c) => c.masterTemplateId)).length;
    return (
      <small className="text-muted ml-2">
        {selected.length} class(es)
        {courseCount > 1 ? ` across ${courseCount} courses` : ""} selected.
      </small>
    );
  }

  renderResults() {
    let { memberRows, hasRun, ranAgainstClassCount } = this.state;

    if (!hasRun) {
      return null;
    }

    if (memberRows.length === 0) {
      return (
        <div className="alert alert-info mt-4" role="alert">
          <strong>No attendance found</strong> for the{" "}
          {ranAgainstClassCount} selected class(es).
          <br />
          <small>
            Attendance only appears here once an admin has marked members as
            Attended for a class. Before the MN-2072 fix, attendance could not be
            marked after a class had ended, so most past classes were never
            marked - those classes will stay empty until they are marked.
          </small>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <ToolkitProvider
          keyField="memberId"
          data={memberRows}
          columns={columns}
          exportCSV={{
            fileName: getExportFileName("csv"),
            exportAll: false,
            onlyExportFiltered: false,
          }}
          search
        >
          {(props) => (
            <div>
              <div className="d-flex justify-content-end">
                <MyExportCSV {...props.csvProps} />
                <MyExportPDF data={memberRows} />
              </div>

              <MySearch {...props.searchProps} />

              <BootstrapTable
                keyField="memberId"
                noDataIndication={() => <NoDataIndication />}
                {...props.baseProps}
              />
            </div>
          )}
        </ToolkitProvider>
      </div>
    );
  }

  render() {
    let { findBy, memberRows, hasRun, isLoadingReport } = this.state;
    let selected = this.getSelectedClasses();

    return (
      <div>
        <div className="btn-group mb-3" role="group" aria-label="Find by">
          <button
            type="button"
            className={
              "btn btn-sm " +
              (findBy === "course" ? "btn-primary" : "btn-outline-primary")
            }
            onClick={() => this.switchFindBy("course")}
          >
            Find by Course
          </button>
          <button
            type="button"
            className={
              "btn btn-sm " +
              (findBy === "class" ? "btn-primary" : "btn-outline-primary")
            }
            onClick={() => this.switchFindBy("class")}
          >
            Find by Class
          </button>
        </div>

        {findBy === "course" ? this.renderCourseMode() : this.renderClassMode()}

        <div className="mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={selected.length === 0 || isLoadingReport}
            onClick={this.runReport}
          >
            {isLoadingReport ? "Running…" : "Run Report"}
          </button>
          {selected.length === 0 ? (
            <small className="text-muted ml-2">
              Select at least one class.
            </small>
          ) : (
            this.renderSelectionSummary()
          )}
        </div>

        {hasRun && memberRows.length > 0 && (
          <h6 className="mt-4 mb-0">
            {memberRows.length} member(s) attended at least one of the{" "}
            {this.state.ranAgainstClassCount} selected class(es).
          </h6>
        )}

        {this.renderResults()}
      </div>
    );
  }
}

export default AttendanceReport;
